import { Locale } from '@headless-workspace/config';
import { Repository, Result, ResultFailureFromApiError, ResultSuccess } from '@headless-workspace/core-domain';
import {
    AuthorizationStrategy,
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
    QueryParamStrategy,
    RefreshTokenStrategy,
} from '@headless-workspace/data';
import { WordingsDTO } from './WordingsDTO';

export interface WordingsRepositorySpec {
    getWordings(locale: Locale): Promise<Result<Record<string, string>>>;
}

export class WordingsRepository extends Repository implements WordingsRepositorySpec {
    path = '/settings/v1/wordings';

    constructor(
        readonly authStrategy: Partial<AuthorizationStrategy> = memoryCacheAuthStrategy,
        readonly refreshTokenStrategy: Partial<RefreshTokenStrategy> = memoryCacheRefreshTokenStrategy,
        readonly queryParamStrategy: Partial<QueryParamStrategy> = memoryCacheQueryParamStrategy,
    ) {
        super(authStrategy, refreshTokenStrategy, queryParamStrategy);
    }

    async getWordings(locale: Locale): Promise<Result<Record<string, string>>> {
        try {
            const response = await this.datasource.getResource(this.path, { locale });

            const parsedResponse = WordingsDTO.parse(response);

            const value = Object.fromEntries(
                parsedResponse.wordings
                    .filter(
                        (wording): wording is { key: string; value: string } =>
                            wording.value !== undefined && wording.value !== null,
                    )
                    .map(({ key, value }) => [key, value]),
            );

            return ResultSuccess(value);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }
}
